/**
 * Persistent, non-blocking banner shown at the top of HomeScreen when the
 * scanned table already has an open bill. Tapping it opens the table order.
 *
 * This is the reachability half of the "auto-open once per order" fix: because
 * Home no longer force-redirects on every mount (see `useActiveTableOrder`),
 * the open bill needs a standing entry point that never traps the guest.
 *
 * Visual structure mirrors `@/components/group/GroupBanner` — dark pill, live
 * dot, title + sub, chevron — so the two dine-in surfaces read as one family.
 */
import { useTranslation } from "react-i18next";

export interface TableBillBannerProps {
  onTap: () => void;
}

export function TableBillBanner({ onTap }: TableBillBannerProps) {
  const { t } = useTranslation();

  return (
    <button
      data-testid="table-bill-banner"
      onClick={onTap}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        width: "calc(100% - 32px)",
        margin: "12px 16px 0",
        padding: "10px 12px",
        background: "var(--ink-1)",
        color: "#fff",
        border: "none",
        borderRadius: 14,
        cursor: "pointer",
        fontFamily: "inherit",
        textAlign: "left",
        boxShadow: "0 6px 20px -8px rgba(0,0,0,0.3)",
      }}
      aria-label={t("v2.dine_in_order.open_bill_cta")}
    >
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          borderRadius: 999,
          background: "var(--primary-400, #34d399)",
          boxShadow: "0 0 0 4px rgba(52, 211, 153, 0.18)",
          flexShrink: 0,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 700 }}>
          {t("v2.dine_in_order.open_bill_title")}
        </div>
        <div
          style={{
            fontSize: 11,
            color: "rgba(255,255,255,0.7)",
            marginTop: 1,
          }}
        >
          {t("v2.dine_in_order.open_bill_sub")}
        </div>
      </div>
      <span
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: "#fff",
          flexShrink: 0,
          whiteSpace: "nowrap",
        }}
      >
        {t("v2.dine_in_order.open_bill_cta")}
      </span>
      <span
        aria-hidden
        style={{ fontSize: 18, color: "rgba(255,255,255,0.7)", flexShrink: 0 }}
      >
        ›
      </span>
    </button>
  );
}
